import "./index.scss";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import AnimateOnChange from "react-animate-on-change";

import Modal from "../components/modal";
import InfoLine from "../components/Marquee";
import {
  Avatar,
  OverseasMark,
  RatingBadge,
  RolePill,
  money,
  shortName,
} from "../components/atoms";
import { tintFor } from "../components/appShell";
import {
  activeBidding,
  activeBlock as getActiveBlock,
  blockStatus,
  lastSoldPlayer,
  mostExpPlayer,
  purseremaining,
  selectactiveBidding,
  selectactiveBlock,
  selectBlockStatus,
  selectLastSoldPlayer,
  selectmostExpPlayer,
  selectPurseRemaining,
} from "../store/features/generalSlice";
import { useSocketRoom } from "../hooks/useSocketRoom";

// A franchise's own colour, wherever the endpoint that fed this screen happens
// to carry it. `color` is an object on the franchise record and a bare field on
// the purse rows, so both shapes are accepted, and a team with no colour set
// falls back to the deterministic tint its name always maps to.
function accentOf(source, name) {
  const c = source?.color ?? source;
  return c?.hex || (typeof c === "string" && c.startsWith("#") ? c : null) ||
    tintFor(name);
}

function useInterval(callback, delay) {
  const intervalRef = React.useRef();
  const callbackRef = React.useRef(callback);

  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  React.useEffect(() => {
    if (typeof delay === "number") {
      intervalRef.current = window.setInterval(
        () => callbackRef.current(),
        delay
      );
      return () => window.clearInterval(intervalRef.current);
    }
  }, [delay]);

  return intervalRef;
}

// The screen the room watches. It is the one page the design system does not
// describe - it is a projected scoreboard, not a page someone reads at a desk -
// so it is built from the system's own tokens rather than new ones: the rail's
// navy as the ground, white and a translucent white as the two surfaces, and
// the meaning colours doing exactly what they do everywhere else.
function Index({ socket }) {
  let { sessionId } = useParams();
  let [showLoading, setShowLoading] = useState(false);
  let [purseChunks, setPurseChunks] = useState([]);
  let [activePurse, setActivePurse] = useState(0);
  let [animatePurse, setAnimatePurse] = useState(false);
  let [animateSockets, setAnimateSockets] = useState({ bid: false });
  let [counter, setCounter] = useState(1);
  let [content, setContent] = useState("Please wait…");
  let dispatch = useDispatch();

  // Rejoins on every reconnect, so a dropped socket recovers on its own.
  useSocketRoom(socket, { role: "public", id: null, sessionId: sessionId });

  let activePlayerData = useSelector(selectactiveBidding);
  let blockActiveStatus = useSelector(selectBlockStatus);
  let lsPlayer = useSelector(selectLastSoldPlayer);
  let MExpPlayer = useSelector(selectmostExpPlayer);
  let purseRemaining = useSelector(selectPurseRemaining);
  let block = useSelector(selectactiveBlock);

  useEffect(() => {
    switch (blockActiveStatus) {
      case "started":
        setContent("Please wait…");
        setShowLoading(false);
        break;
      case "stopped":
        setContent("Paused. Waiting for the curator to resume.");
        setShowLoading(true);
        break;
      case "ready":
        setContent("The curator is choosing the next category.");
        setShowLoading(false);
        break;
      default:
        setContent("Please wait…");
        setShowLoading(false);
    }
  }, [blockActiveStatus]);

  const refresh = () => {
    dispatch(activeBidding(sessionId));
    dispatch(mostExpPlayer(sessionId));
    dispatch(purseremaining(sessionId));
    dispatch(lastSoldPlayer(sessionId));
    dispatch(blockStatus(sessionId));
    dispatch(getActiveBlock(sessionId));
  };

  useEffect(refresh, [counter]);
  useEffect(refresh, []);

  useEffect(() => {
    // Joining is handled by useSocketRoom, which also survives a reconnect.
    socket.on("block-started", (playerType) => {
      setShowLoading(true);
      setContent(`${String(playerType || "").trim()} category started`);
      setTimeout(() => {
        setShowLoading(false);
        setContent("Waiting for the next player…");
      }, 3000);
    });

    socket.on("block-paused", () => {
      dispatch(activeBidding(sessionId));
      dispatch(blockStatus(sessionId));
    });

    socket.on("block-finished", (playerType) => {
      dispatch(mostExpPlayer(sessionId));
      dispatch(lastSoldPlayer());
      setShowLoading(true);
      setContent(`${String(playerType || "").trim()} category finished`);
      dispatch(activeBidding(sessionId));
      dispatch(blockStatus(sessionId));
      setCounter((a) => a + 1);
      setTimeout(() => setShowLoading(false), 3000);
    });

    socket.on("bid-updated", () => {
      dispatch(mostExpPlayer(sessionId));
      dispatch(lastSoldPlayer(sessionId));
      dispatch(activeBidding(sessionId));
      dispatch(blockStatus(sessionId));
      setDemoBid();
      setCounter((a) => a + 1);
    });

    socket.on("set-bid-reset", () => {
      dispatch(mostExpPlayer(sessionId));
      dispatch(lastSoldPlayer(sessionId));
      setContent("Updating…");
      setShowLoading(true);
      setTimeout(() => setShowLoading(false), 1000);
      setCounter((a) => a + 1);
    });

    socket.on("rtm-updated", () => {
      dispatch(activeBidding(sessionId));
      dispatch(blockStatus(sessionId));
      setContent(<span className="call-note success">Right to match used</span>);
      setShowLoading(true);
      setTimeout(() => setShowLoading(false), 3000);
      setCounter((a) => a + 1);
    });

    socket.on("set-player-unsold", () => {
      setContent(
        <div className="call">
          <span className="call-note danger">Teams ne aukaat dikha di</span>
          <span className="call-verdict danger">Unsold</span>
        </div>
      );
      setShowLoading(true);
      dispatch(activeBidding(sessionId));
      dispatch(blockStatus(sessionId));
      setCounter((a) => a + 1);
      setTimeout(() => setShowLoading(false), 3000);
    });

    socket.on("set-player-sold", (player) => {
      dispatch(activeBidding(sessionId));
      dispatch(blockStatus(sessionId));
      dispatch(mostExpPlayer(sessionId));
      dispatch(lastSoldPlayer(sessionId));

      setCounter((a) => a + 1);
      setShowLoading(true);
      setContent(
        <div className="call">
          <span className="call-note success">{money(player?.currentBid)}</span>
          <span className="call-verdict success">Sold</span>
          <span className="call-team">{player?.lastBidBy?.team || "—"}</span>
        </div>
      );
      setTimeout(() => setShowLoading(false), 5000);
    });
  }, []);

  // The purse board shows three teams at a time and cycles, so a full league
  // fits on screen without shrinking the numbers to nothing.
  useEffect(() => {
    const perChunk = 3;
    if (purseRemaining[0]) {
      setPurseChunks(
        purseRemaining.reduce((acc, item, index) => {
          const chunk = Math.floor(index / perChunk);
          if (!acc[chunk]) acc[chunk] = [];
          acc[chunk].push(item);
          return acc;
        }, [])
      );
    }
  }, [purseRemaining]);

  const nextPurseQueue = () => {
    setAnimatePurse(true);
    setActivePurse((i) => (i < purseChunks.length - 1 ? i + 1 : 0));
    window.setTimeout(() => setAnimatePurse(false), 500);
  };

  const setDemoBid = () => {
    setAnimateSockets((prev) => ({ ...prev, bid: true }));
    window.setTimeout(
      () => setAnimateSockets((prev) => ({ ...prev, bid: false })),
      400
    );
  };

  useInterval(() => {
    if (purseChunks[0]) nextPurseQueue();
  }, 4000);

  const player = activePlayerData;
  const bidder = player?.lastBidBy;

  // Who is still to come. The curator draws from this pool at random, so there
  // is no running order to show; the board sorts by base price instead, which
  // puts the names the room is waiting for at the top of the tile.
  const upcoming = React.useMemo(() => {
    const pool = Array.isArray(block?.players) ? block.players : [];
    return pool
      .filter((p) => p?.name)
      .slice()
      .sort(
        (a, b) =>
          (b?.basePrice || 0) - (a?.basePrice || 0) ||
          (b?.rating || 0) - (a?.rating || 0)
      );
  }, [block]);

  // How far through the running category the room is.
  const soldCount = block?.soldPlayers?.length || 0;
  const unsoldCount = block?.unsoldPlayers?.length || 0;
  const leftCount =
    (block?.players?.length || 0) + (block?.startedPlayers?.length || 0);

  return (
    <div className="live">
      <Modal show={showLoading} content={content} />

      <InfoLine text="Live auction" />

      {/* What is being auctioned and how far through it the room is. On a board
          people glance at, "18 of 45 gone" answers the question they actually
          have far better than a category name on its own. */}
      <header className="live-head">
        <div className="live-head-block">
          <span className="live-head-label">Now auctioning</span>
          <span className="live-head-value">
            {block?.playerType ? block.playerType.trim() : "—"}
          </span>
        </div>

        <div className="live-head-counts">
          <LiveCount label="Sold" value={soldCount} tone="success" />
          <LiveCount label="Unsold" value={unsoldCount} tone="danger" />
          <LiveCount label="Left" value={leftCount} />
        </div>

        <div className="live-head-links">
          <Link className="live-head-link" to={`/playerlist/${sessionId}`}>
            Player list
          </Link>
          <Link className="live-head-link" to={`/teams/${sessionId}`}>
            Teams
          </Link>
        </div>
      </header>

      <div className="live-body">
        <aside className="live-side">
          <ResultTile label="Most expensive" player={MExpPlayer} />
          <ResultTile label="Last sold" player={lsPlayer} />
        </aside>

        <main className="live-stage">
          {player?.name ? (
            <>
              <div className="live-photo">
                {player?.image ? (
                  <img src={player.image} alt="" />
                ) : (
                  <Avatar name={player?.name} size={120} />
                )}
              </div>

              <h1 className="live-name">
                {shortName(player?.name)}
                <OverseasMark nationality={player?.nationality} />
              </h1>

              <div className="live-meta">
                <RolePill role={player?.role} />
                <RatingBadge value={player?.rating} />
                <span className="live-base">
                  Base {money(player?.basePrice)}
                </span>
              </div>

              <AnimateOnChange
                animationClassName="live-bid-hit"
                animate={animateSockets.bid}
              >
                <div className="live-bid">
                  <span className="live-bid-label">Current bid</span>
                  <span className="live-bid-value">
                    {money(player?.currentBid)}
                  </span>
                </div>
              </AnimateOnChange>

              <div
                className="live-bidder"
                style={
                  bidder?.team
                    ? { "--accent": accentOf(bidder?.franchise, bidder.team) }
                    : undefined
                }
              >
                {bidder?.team ? (
                  <>
                    <Avatar
                      name={bidder.team}
                      src={bidder?.franchise?.image}
                      size={36}
                      logo
                    />
                    <span className="live-bidder-team">{bidder.team}</span>
                  </>
                ) : (
                  <span className="live-bidder-none">No bids yet</span>
                )}
              </div>
            </>
          ) : (
            <p className="live-idle">Waiting for the next player…</p>
          )}
        </main>

        <aside className="live-side live-side-right">
          <section className="live-tile live-purse">
            <span className="live-tile-label">Purse remaining</span>
            {purseChunks[activePurse]?.map((p) => (
              <AnimateOnChange
                key={p?.name}
                animationClassName="live-fade"
                animate={animatePurse}
              >
                <div
                  className="live-purse-row"
                  style={{ "--accent": accentOf(p, p?.name) }}
                >
                  <Avatar name={p?.name} src={p?.image} size={52} logo />
                  <div className="live-purse-text">
                    <span className="live-purse-team">{p?.name}</span>
                    <span className="live-purse-value">{money(p?.purse)}</span>
                  </div>
                </div>
              </AnimateOnChange>
            ))}
            {!purseChunks[0] && <p className="live-tile-none">No teams yet</p>}
          </section>

          <UpcomingPool players={upcoming} running={Boolean(block?._id)} />
        </aside>
      </div>
    </div>
  );
}

// A count on the header strip.
function LiveCount({ label, value, tone }) {
  return (
    <div className={`live-count ${tone || ""}`}>
      <span className="live-count-value">{value}</span>
      <span className="live-count-label">{label}</span>
    </div>
  );
}

// The pool still to come, under the purse board. The room's second question,
// after what a team can still spend, is who is left to spend it on - and until
// now that answer lived a page away behind the "Player list" link.
//
// It is a preview, not the list: the tile shows the top of the pool and counts
// the rest, because a board people read from across a room cannot be scrolled.
// Six is what fits under the purse board on a 1080p projector without pushing
// the stage off the bottom of the screen.
const POOL_PREVIEW = 6;

function UpcomingPool({ players, running }) {
  const rows = Array.isArray(players) ? players : [];
  const shown = rows.slice(0, POOL_PREVIEW);
  const rest = rows.length - shown.length;

  return (
    <section className="live-tile live-pool">
      <span className="live-tile-label">
        Still to come
        {rows.length ? <span className="live-pool-count">{rows.length}</span> : null}
      </span>

      {shown.length ? (
        <>
          <ul className="live-pool-list">
            {shown.map((p) => (
              <li className="live-pool-row" key={p?._id || p?.name}>
                <Avatar name={p?.name} src={p?.image} size={30} />
                <div className="live-pool-text">
                  <span className="live-pool-name">
                    {shortName(p?.name)}
                    <OverseasMark nationality={p?.nationality} />
                  </span>
                  <span className="live-pool-role">{p?.role || "—"}</span>
                </div>
                <span className="live-pool-base">{money(p?.basePrice)}</span>
              </li>
            ))}
          </ul>

          {rest > 0 && (
            <p className="live-pool-more">and {rest} more in this category</p>
          )}
        </>
      ) : (
        // Two different empties. Before the curator has started anything there
        // is no category to be left in, and saying "nobody left" read as the
        // pool having gone missing rather than the auction not having begun.
        <p className="live-tile-none">
          {running
            ? "Nobody left in this category"
            : "Waiting for the curator to start a category"}
        </p>
      )}
    </section>
  );
}

// Where a player went, said in the buying team's own colour. The accent is the
// left edge of the tile and the dot beside the team name, so the answer is
// readable across a room before any of the text is.
function ResultTile({ label, player }) {
  const team = player?.lastBidBy?.team;
  const accent = accentOf(player?.lastBidBy?.franchise, team);

  return (
    <section
      className={`live-tile ${team ? "has-accent" : ""}`}
      style={team ? { "--accent": accent } : undefined}
    >
      <span className="live-tile-label">{label}</span>
      {player?.name ? (
        <div className="live-tile-body">
          <Avatar
            name={team || player?.name}
            src={player?.lastBidBy?.franchise?.image}
            size={40}
            logo
          />
          <div className="live-tile-text">
            <span className="live-tile-name">
              {shortName(player?.name)}
              <OverseasMark nationality={player?.nationality} />
            </span>
            <span className="live-tile-team">
              <span className="live-tile-dot" />
              {team || "—"}
              {player?.role ? ` · ${player.role}` : ""}
            </span>
          </div>
          <span className="live-tile-value">{money(player?.soldAt)}</span>
        </div>
      ) : (
        <p className="live-tile-none">Nothing sold yet</p>
      )}
    </section>
  );
}

export default Index;
